import {
  derivePaymentModel,
  PaymentModel,
} from "@redotech/redo-return-coverage-common/coverage";
import type { Settings } from "./team";

/**
 * Controls when a storefront product is rendered on the storefront. This layers
 * *on top of* each product's existing enabled flags — a product renders only if
 * it is both enabled AND its publish status gate passes. Maps directly onto the
 * `ThemePublishedStatus` (active/draft) the storefront extension already
 * detects.
 */
export enum StorefrontPublishStatus {
  /** Never render on the storefront (except in preview mode). A kill switch. */
  DISABLED = "disabled",
  /** Render only on unpublished/draft (staging) themes, hidden on the live site. */
  DRAFT_THEMES = "draft_themes",
  /** Render everywhere — the live site and draft themes. */
  LIVE_SITE = "live_site",
}

/**
 * Keys of the products tracked by {@link StorefrontPublishStatusMap}. Adding a
 * new storefront product is a matter of adding a key here and an entry to the
 * {@link STOREFRONT_PRODUCTS} registry below.
 */
export type StorefrontProductKey =
  | "coverage"
  | "marketing"
  | "loyalty"
  | "reviews"
  | "conversion"
  | "hsa";

/**
 * Per-product storefront publish status. Absent keys are treated as
 * {@link StorefrontPublishStatus.LIVE_SITE} so existing teams keep rendering
 * exactly as they do today.
 */
export type StorefrontPublishStatusMap = Partial<
  Record<StorefrontProductKey, StorefrontPublishStatus>
>;

/**
 * Resolve the publish status for a single product from a team's
 * {@link StorefrontPublishStatusMap}, defaulting to LIVE_SITE.
 */
export function getStorefrontPublishStatus(
  map: StorefrontPublishStatusMap | undefined,
  product: StorefrontProductKey,
): StorefrontPublishStatus {
  return map?.[product] ?? StorefrontPublishStatus.LIVE_SITE;
}

/**
 * Describes one storefront product for the publish-status registry. Adding a
 * new product to the storefront publish surface is a one-liner: add a key to
 * {@link StorefrontProductKey} and an entry to {@link STOREFRONT_PRODUCTS}.
 */
function isExclusiveMerchantPaidPackageProtection(
  settings: Settings["packageProtection"],
): boolean {
  return (
    settings?.packageProtectionMerchantPaidCartThreshold == null &&
    (!!settings?.packageProtectionPlusMerchantPaidPerOrderEnabled ||
      derivePaymentModel(
        settings?.packageProtectionPlusMerchantPaidEnabledDates,
      ) === PaymentModel.MERCHANT_PAID)
  );
}

export interface StorefrontProductDefinition {
  key: StorefrontProductKey;
  /** Human-readable product name shown in the admin summary/controls. */
  label: string;
  /** Whether this product is configurable from the per-product publish UI. */
  editable: boolean;
  /**
   * Reads whether the product is enabled at all (independent of publish
   * status). Used by the admin summary card to know which products are live.
   */
  isEnabled: (settings: Settings) => boolean;
  /**
   * Returns short labels for the storefront-facing "things" this product has
   * active (e.g. returns → "Coverage", "Shop on site"). Drives the summary
   * card's per-product detail line. Empty when nothing storefront-facing is on.
   */
  activeThings: (settings: Settings) => string[];
}

/**
 * Registry of storefront products. The shared source of truth for both the
 * storefront render gate and the admin "Storefront publishing" summary card.
 *
 * `editable` controls whether the admin card exposes a publish-status dropdown
 * for the product (flip it per product to turn the switch on). Today only
 * returns coverage is editable; the others are status-only and are published by
 * enabling/disabling the product. All default to LIVE_SITE so existing teams
 * render unchanged.
 */
export const STOREFRONT_PRODUCTS: StorefrontProductDefinition[] = [
  {
    key: "coverage",
    label: "Coverage",
    editable: true,
    isEnabled: (s) => {
      const returnsMerchantPaid =
        derivePaymentModel(s.merchantPaidEnabledDates) ===
        PaymentModel.MERCHANT_PAID;
      const ppMerchantPaid = isExclusiveMerchantPaidPackageProtection(
        s.packageProtection,
      );

      return (
        (!!s.returns?.enabled && !!s.coverageEnabled && !returnsMerchantPaid) ||
        (!!s.packageProtection?.enabled && !ppMerchantPaid) ||
        !!s.returns?.finalSaleReturns?.enabled
      );
    },
    activeThings: (s) => {
      const things: string[] = [];
      if (!!s.returns?.enabled && !!s.coverageEnabled) {
        things.push("Coverage");
      }
      if (s.packageProtection?.enabled) {
        things.push("Package protection");
      }
      if (s.returns?.finalSaleReturns?.enabled) {
        things.push("Final sale returns");
      }
      // "Shop on site" — the on-site shopping experience surfaced by returns.
      if (s.customerWidget?.general?.enableInAppEmbed) {
        things.push("Shop on site");
      }
      return things;
    },
  },
  {
    key: "marketing",
    label: "Marketing",
    editable: false,
    isEnabled: (s) => !!s.marketing?.enabled,
    activeThings: (s) => {
      const things: string[] = [];
      const marketing = s.marketing;
      if (!marketing?.enabled) {
        return things;
      }
      if (marketing.textsEnabled) {
        things.push("SMS");
      }
      if (marketing.campaigns?.enabled) {
        things.push("Campaigns");
      }
      if (marketing.backInStock?.buttonSelector) {
        things.push("Back in stock");
      }
      if (marketing.spotlightsEnabled) {
        things.push("Spotlights");
      }
      return things;
    },
  },
  {
    key: "loyalty",
    label: "Loyalty",
    editable: false,
    isEnabled: (s) => !!s.marketing?.loyalty?.enabled,
    activeThings: (s) => (s.marketing?.loyalty?.enabled ? ["Rewards"] : []),
  },
  {
    key: "reviews",
    label: "Reviews",
    editable: false,
    isEnabled: (s) => !!s.reviews?.enabled,
    activeThings: (s) => (s.reviews?.enabled ? ["On-site reviews"] : []),
  },
  {
    key: "conversion",
    label: "Conversion",
    editable: false,
    isEnabled: (s) =>
      !!s.checkoutOptimization?.enabled || !!s.predictiveConversion?.enabled,
    activeThings: (s) => {
      const things: string[] = [];
      if (s.checkoutOptimization?.enabled) {
        things.push("Checkout optimization");
      }
      if (s.predictiveConversion?.enabled) {
        things.push("Predictive conversion");
      }
      return things;
    },
  },
  {
    key: "hsa",
    label: "HSA/FSA",
    editable: false,
    isEnabled: (s) => !!s.hsa?.enabled,
    activeThings: (s) => (s.hsa?.enabled ? ["HSA/FSA payments"] : []),
  },
];

/**
 * Index into {@link STOREFRONT_PRODUCTS} by key for direct lookups.
 */
export const STOREFRONT_PRODUCTS_BY_KEY: Record<
  StorefrontProductKey,
  StorefrontProductDefinition
> = Object.fromEntries(
  STOREFRONT_PRODUCTS.map((product) => [product.key, product]),
) as Record<StorefrontProductKey, StorefrontProductDefinition>;

/**
 * Whether a given product has any storefront-facing functionality enabled.
 * Convenience wrapper over the registry's `isEnabled` reader.
 */
export function isStorefrontProductEnabled(
  settings: Settings,
  product: StorefrontProductKey,
): boolean {
  return STOREFRONT_PRODUCTS_BY_KEY[product].isEnabled(settings);
}
